import {
  Box, Button, VStack, HStack, Text, Badge, Divider, Link, useColorModeValue,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as TaskService from 'services/TaskService';
import { Task, TaskStatus } from 'utils/interfaces';
import { buildMapsUrl } from 'utils/common-utils';

const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  DA_INIZIARE: 'Da iniziare',
  IN_CORSO: 'In corso',
  COMPLETATO: 'Completato',
};

const formatDateDDmmYYYY = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface EmployeeTasksCardProps {
  task: Task;
  onTaskUpdate: () => void;
}

export default function EmployeeTasksCardComponent({ task, onTaskUpdate }: EmployeeTasksCardProps) {
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const history = useHistory();

  const shadowBox = useColorModeValue('0 2px 8px rgba(0,0,0,0.1)', 'none');

  // Only DA_INIZIARE -> IN_CORSO is a real status PATCH here. Completing an intervention no longer
  // flips the task to COMPLETATO client-side — that used to strand the employee if they abandoned the
  // report form (crash, "Annulla", back button), since a COMPLETATO task with no report had no way
  // back in. The task stays IN_CORSO until the report is actually submitted; the backend marks it
  // COMPLETATO as part of a successful report creation.
  const handleStartTask = async () => {
    if (!token) return;
    try {
      // No effectiveStartDateTime here: the backend already defaults it to its own (timezone-correct)
      // LocalDateTime.now() when unset, same as effectiveEndDateTime on report submission. Sending
      // new Date().toISOString() (UTC) used to get deserialized straight into that LocalDateTime
      // field with no zone conversion, landing hours behind the real local time.
      const res = await TaskService.updateTaskStatus(
        token,
        () => keycloak?.logout(),
        task.id,
        { status: 'IN_CORSO' }
      );
      if (res) {
        toast.success('Stato aggiornato');
        onTaskUpdate();
      }
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento dello stato');
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'COMPLETATO':
        return 'green';
      case 'IN_CORSO':
        return 'orange';
      case 'DA_INIZIARE':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const isDaIniziare = task.status === 'DA_INIZIARE';
  const canViewReport = task.status === 'COMPLETATO' && task.hasInterventionReport && !!task.interventionReportId;
  // Covers both the normal IN_CORSO case and a task stuck COMPLETATO-without-report from before this fix.
  const needsReport = !isDaIniziare && !task.hasInterventionReport;

  const getButtonLabel = () => {
    if (canViewReport) return 'Vedi Rapporto d\'Intervento';
    if (needsReport) return 'Completa Intervento';
    return 'Inizia Intervento';
  };

  const handleActionButtonClick = () => {
    if (canViewReport) {
      history.push(`/dipendente/rapporti-intervento/${task.interventionReportId}`);
    } else if (needsReport) {
      history.push(`/dipendente/interventi/${task.id}/rapporto`);
    } else {
      handleStartTask();
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      boxShadow={shadowBox}
      p={4}
      mb={4}
      borderLeft="4px solid"
      borderLeftColor={`${getStatusColor(task.status)}.400`}
    >
      <VStack align="start" spacing={3} w="full">
        {/* Task Title and Customer */}
        <VStack align="start" spacing={1} w="full">
          <Text fontWeight="bold" fontSize="md" w="full">
            {task.title}
          </Text>
          {task.address && (
            <Link href={buildMapsUrl(task.address)} isExternal fontSize="sm" color="blue.500" textDecoration="underline">
              {task.address}
            </Link>
          )}
          <Text fontSize="sm" color="gray.500" fontWeight="500">
            {task.customer.fullName}
          </Text>
        </VStack>

        <Divider />

        {/* Dates Section */}
        <VStack align="start" spacing={2} w="full" fontSize="sm">
          <Box>
            <Text fontWeight="500" color="gray.700">Programmato per:</Text>
            <Text color="gray.600">
              {formatDateDDmmYYYY(task.scheduledDateTime)} alle {formatTime(task.scheduledDateTime)}
            </Text>
          </Box>

          {task.effectiveStartDateTime && (
            <Box>
              <Text fontWeight="500" color="gray.700">Inizio effettivo:</Text>
              <Text color="gray.600">
                {formatDateDDmmYYYY(task.effectiveStartDateTime)} alle {formatTime(task.effectiveStartDateTime)}
              </Text>
            </Box>
          )}

          {task.effectiveEndDateTime && (
            <Box>
              <Text fontWeight="500" color="gray.700">Fine effettiva:</Text>
              <Text color="gray.600">
                {formatDateDDmmYYYY(task.effectiveEndDateTime)} alle {formatTime(task.effectiveEndDateTime)}
              </Text>
            </Box>
          )}
        </VStack>

        <Divider />

        {/* Status and Action Button */}
        <HStack w="full" justify="space-between" align="center">
          <Badge colorScheme={getStatusColor(task.status)} fontSize="sm" px={3} py={1}>
            {TASK_STATUS_LABELS[task.status]}
          </Badge>
          <Button
            size="sm"
            onClick={handleActionButtonClick}
            backgroundColor="#688CC8"
            color="white"
            _hover={{ bg: 'gray.700' }}
            _active={{ bg: 'gray.600' }}
            _focus={{ boxShadow: 'none' }}
          >
            {getButtonLabel()}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
